<template>
  <div class="card shadow-sm p-3">
    <div class="d-flex align-items-center gap-2 mb-3">
      <span class="material-icons text-danger">picture_as_pdf</span>
      <span class="fw-bold">Validación CV — Planilla {{ numero }}</span>
    </div>

    <button
      class="btn btn-primary btn-sm d-flex align-items-center gap-1"
      @click="validarCv"
      :disabled="procesando"
    >
      <span v-if="procesando" class="spinner-border spinner-border-sm"></span>
      <span v-else class="material-icons" style="font-size:16px">verified</span>
      {{ procesando ? 'Validando...' : 'Validar CV' }}
    </button>

    <!-- Error archivo -->
    <div v-if="errorMensaje" class="alert alert-danger small mt-3">
      <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">error</span>
      {{ errorMensaje }}
    </div>

    <!-- Resultado -->
    <div v-if="resultado" class="mt-3">
      <hr />

      <!-- Alerta resumen -->
      <div class="alert py-2 small" :class="alertaClase">
        <span class="material-icons me-1" style="font-size:15px;vertical-align:middle">
          {{ todoCoincide ? 'check_circle' : 'warning' }}
        </span>
        {{ todoCoincide
            ? 'Todos los datos del CV coinciden con la base de datos.'
            : 'Se encontraron diferencias entre el CV y la base de datos.' }}
      </div>

      <!-- Tabla comparación -->
      <table class="table table-sm table-bordered small">
        <thead class="table-dark">
          <tr>
            <th>Campo</th>
            <th>Base de datos</th>
            <th>PDF</th>
            <th style="width:50px" class="text-center">OK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fila in filasComparacion" :key="fila.campo">
            <td class="fw-bold">{{ fila.campo }}</td>
            <td>{{ fila.bd ?? '-' }}</td>
            <td>{{ fila.pdf ?? '-' }}</td>
            <td class="text-center">
              <span v-if="fila.coincide === true"  class="badge bg-success">✓</span>
              <span v-else-if="fila.coincide === false" class="badge bg-danger">✗</span>
              <span v-else class="text-muted small">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Diagnósticos -->
      <p class="fw-bold small mb-2 mt-3">Diagnósticos CIE-10</p>

      <div class="mb-1 small">
        <span class="fw-bold text-success me-1">✓ Coinciden:</span>
        <template v-if="resultado.diagnosticosCoinciden?.length">
          <span v-for="d in resultado.diagnosticosCoinciden" :key="d"
            class="badge bg-success me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-warning me-1">⚠ En BD, no en PDF:</span>
        <template v-if="resultado.diagnosticosNoPdf?.length">
          <span v-for="d in resultado.diagnosticosNoPdf" :key="d"
            class="badge bg-warning text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <div class="mb-1 small">
        <span class="fw-bold text-info me-1">⚠ En PDF, no en BD:</span>
        <template v-if="resultado.diagnosticosNoBd?.length">
          <span v-for="d in resultado.diagnosticosNoBd" :key="d"
            class="badge bg-info text-dark me-1">{{ d }}</span>
        </template>
        <span v-else class="text-muted">Ninguno</span>
      </div>

      <!-- Texto PDF colapsable -->
      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm"
          @click="mostrarTexto = !mostrarTexto">
          <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">
            {{ mostrarTexto ? 'expand_less' : 'expand_more' }}
          </span>
          {{ mostrarTexto ? 'Ocultar texto PDF' : 'Ver texto completo del PDF' }}
        </button>
        <textarea v-if="mostrarTexto"
          class="form-control form-control-sm mt-2"
          rows="6" :value="resultado.textoCompleto" readonly>
        </textarea>
      </div>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "PdfValidador",
  props: {
    idTramite: { type: Number, required: true },
    numero:    { type: String, required: true },
    autoValidar: { type: Boolean, default: false },
  },

  emits: ['resultado'],

  data() {
    return {
      resultado:    null,
      procesando:   false,
      errorMensaje: null,
      mostrarTexto: false,
    };
  },
  computed: {
  todoCoincide() {
    return this.resultado?.codigoValCoincide
        && this.resultado?.cedulaCoincide
        && this.resultado?.nombreCoincide
        && this.resultado?.fechaDesdeCoincide
        && this.resultado?.fechaHastaCoincide;
  },
    alertaClase() {
      return this.todoCoincide ? 'alert-success' : 'alert-warning';
    },
filasComparacion() {
  if (!this.resultado) return [];
  return [
    {
      campo:    'Cód. Validación',
      bd:       this.resultado.codValBd,
      pdf:      this.resultado.codigoValidacionPdf,
      coincide: this.resultado.codigoValCoincide,
    },
    {
      campo:    'Cédula',
      bd:       this.resultado.cedulaBd,
      pdf:      this.resultado.cedulaPdf,
      coincide: this.resultado.cedulaCoincide,
    },
    {
      campo:    'Nombre',
      bd:       this.resultado.nombreBd,
      pdf:      this.resultado.nombrePdf,
      coincide: this.resultado.nombreCoincide,
    },
    {
      campo:    'Fecha Desde',
      bd:       this.resultado.fechaDesdeBd,
      pdf:      this.resultado.fechaDesdePdf,
      coincide: this.resultado.fechaDesdeCoincide,
    },
    {
      campo:    'Fecha Hasta',
      bd:       this.resultado.fechaHastaBd,
      pdf:      this.resultado.fechaHastaPdf,
      coincide: this.resultado.fechaHastaCoincide,
    },
    // ── Solo informativo, sin comparación ─────────────────
    {
      campo:    'Categoría',
      bd:       '-',
      pdf:      this.resultado.categoriaPdf,
      coincide: null,   // null = no aplica
    },
    {
      campo:    'Tipo',
      bd:       '-',
      pdf:      this.resultado.tipoPdf,
      coincide: null,
    },
  ];
},
  },

  mounted() {
    if (this.autoValidar) this.validarCv();
  },

  methods: {
    async ejecutarValidacion() {
      await this.validarCv();
	},

    async validarCv() {
      this.procesando   = true;
      this.resultado    = null;
      this.errorMensaje = null;
      this.mostrarTexto = false;
      try {
        const resp = await api.get(`/validacion-cv/${this.idTramite}/${this.numero}`);
        const v    = resp.data;
        if (!v.archivoEncontrado) {
          this.errorMensaje = v.mensajeError ?? "Archivo CV.pdf no encontrado.";
          this.$emit('resultado', 'sin-archivo');
          return;
        }
        this.resultado = v;
        this.$emit('resultado', this.calcularEstado());
      } catch (e) {
        console.error(e);
        this.$emit('resultado', 'error');
        Swal.fire({ icon: "error", title: "Error", text: "No se pudo validar el CV." });
      } finally {
        this.procesando = false;
      }
    },

    calcularEstado() {
      if (!this.resultado) return null;
      if (!this.resultado.archivoEncontrado) return 'sin-archivo';

      const hayError =
        !this.resultado.codigoValCoincide ||
        !this.resultado.cedulaCoincide;

      const hayLeve =
        this.resultado.diagnosticosNoBd?.length > 0 ||
        this.resultado.diagnosticosNoPdf?.length > 0 ||
        !this.resultado.nombreCoincide;

      if (hayError) return 'error';
      if (hayLeve)  return 'leve';
      return 'ok';
    },
  },
};
</script>